import { useEffect, useMemo, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import ContainerPage from "../../components/Containers/ContainerPage";
import HealthSummaryCards from "./components/HealthSummaryCards";
import ClientsTable from "./components/ClientsTable";
import ModalCreateBrand from "./components/ModalCreateBrand";
import AnnotationsSidebar from "./components/AnnotationsSidebar";
import useClientsList from "../../hooks/useClientsList";
import { useInView } from "react-intersection-observer";
import {
  ADMIN_DASHBOARD_SEARCH_PLACEHOLDER,
  ADMIN_DASHBOARD_FILTER_STATUS_PLACEHOLDER,
  ADMIN_DASHBOARD_CREATE_BRAND_BUTTON,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import TextField from "../../components/Form/TextField";
import Select from "../../components/Form/Select";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import { OWNER_DASHBOARD_FILTER_STATUS_OPTIONS } from "../../utils/constants";
import Button from "../../components/Buttons/Button";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import {
  startListenerAnnotationReadState,
  stopListenerAnnotationReadState,
  startGetOwners,
} from "../../actions/annotations";

const AdminDashboard = () => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const currentOwnerUid = useSelectorApp((state) => state.auth.user?.uid);

  // Use the custom hook for clients management - simplified return
  const {
    displayedClients,
    groupedClients,
    healthSummaryGroups,
    hasMoreItems,
    isLoadingMore,
    loadMoreClients,
    totalClients,
    isLoading,
    filterStatus,
    setFilterStatus,
    searchTerm,
    setSearchTerm,
  } = useClientsList();

  const [isCreateBrandModalOpen, setIsCreateBrandModalOpen] = useState(false);
  const [selectedAnnotationTarget, setSelectedAnnotationTarget] =
    useState(null);
  const [annotationReadStateByScope, setAnnotationReadStateByScope] = useState(
    {}
  );
  const [ownersByID, setOwnersByID] = useState({});
  const annotationReadStateListenerRef = useRef(null);

  const onSelectAnnotationTarget = (target) => {
    setSelectedAnnotationTarget(target);
  };

  // Local state for search input with debounce
  const [localSearchTerm, setLocalSearchTerm] = useState("");
  const debouncedSearchRef = useRef(null);
  const lastSentSearchRef = useRef("");
  const filterStatusRef = useRef(filterStatus);
  filterStatusRef.current = filterStatus;

  // React Hook Form setup for common components
  const { control, setValue, watch } = useForm({
    defaultValues: {
      searchTerm: localSearchTerm,
      filterStatus:
        filterStatus === null ? "" : filterStatus ? "active" : "inactive",
    },
  });

  // Refs para evitar loops en sincronización
  const isInternalUpdateRef = useRef(false);

  // Sync external searchTerm → react-hook-form (when cleared externally)
  useEffect(() => {
    // Only sync if searchTerm was cleared or changed externally (not from our debounced update)
    if (
      !isInternalUpdateRef.current &&
      searchTerm !== lastSentSearchRef.current &&
      searchTerm !== localSearchTerm
    ) {
      setLocalSearchTerm(searchTerm);
      setValue("searchTerm", searchTerm);
      lastSentSearchRef.current = searchTerm;
    }
  }, [searchTerm, localSearchTerm, setValue]);

  // Sync external filterStatus → react-hook-form
  useEffect(() => {
    if (!isInternalUpdateRef.current) {
      const formFilterValue =
        filterStatus === null ? "" : filterStatus ? "active" : "inactive";
      const currentFormValue = watch("filterStatus");
      if (currentFormValue !== formFilterValue) {
        setValue("filterStatus", formFilterValue);
      }
    }
  }, [filterStatus, setValue, watch]);

  // Debounce implementation: update searchTerm in hook only after 350ms delay
  useEffect(() => {
    if (debouncedSearchRef.current) {
      clearTimeout(debouncedSearchRef.current);
    }

    debouncedSearchRef.current = setTimeout(() => {
      lastSentSearchRef.current = localSearchTerm;
      isInternalUpdateRef.current = true;
      const trimmed = String(localSearchTerm).trim();
      // Clear plan filter together with searchTerm so useClientsList does not
      // refetch with filterValue=null before the debounced search string arrives.
      if (trimmed.length > 0 && filterStatusRef.current !== null) {
        setFilterStatus(null);
        setValue("filterStatus", "");
      }
      setSearchTerm(localSearchTerm);
      isInternalUpdateRef.current = false;
    }, 350);

    return () => clearTimeout(debouncedSearchRef.current);
  }, [localSearchTerm, setSearchTerm, setFilterStatus, setValue]);

  // Handler para cambios en el campo de búsqueda
  const onSearchChange = (value) => {
    setLocalSearchTerm(value);
  };

  // Handler para cambios en el filtro de estado
  const onFilterStatusChange = (value) => {
    isInternalUpdateRef.current = true;

    // Limpiar búsqueda antes de cambiar el filtro de estado
    if (searchTerm.length > 0 || localSearchTerm.length > 0) {
      setSearchTerm("");
      setLocalSearchTerm("");
      setValue("searchTerm", "");
      lastSentSearchRef.current = "";
    }

    // Cambiar el filtro de estado
    if (value === "") {
      setFilterStatus(null);
    } else if (value === "active") {
      setFilterStatus(true);
    } else {
      setFilterStatus(false);
    }

    isInternalUpdateRef.current = false;
  };

  // Intersection Observer for infinite scroll
  const { ref: lastItemRef, inView } = useInView({
    triggerOnce: false,
    rootMargin: "50px 0px",
  });
  const loadMoreClientsRef = useRef(loadMoreClients);
  loadMoreClientsRef.current = loadMoreClients;

  // Handle infinite scroll — keep loadMoreClients out of deps so health-score
  // Redux updates do not re-trigger pagination while the sentinel stays in view.
  useEffect(() => {
    if (!inView || !hasMoreItems || isLoadingMore) return;
    loadMoreClientsRef.current();
  }, [inView, hasMoreItems, isLoadingMore]);

  // One summary unit per plan-active agency group (multi-advertiser uses average score).
  const healthSummary = useMemo(() => {
    let healthy = 0;
    let watchlist = 0;
    let atRisk = 0;

    (healthSummaryGroups || []).forEach((group) => {
      if (
        group.isHealthSummaryLoading ||
        group.healthSummaryScore === null ||
        group.healthSummaryScore === undefined
      ) {
        return;
      }

      const score = group.healthSummaryScore;
      if (score >= 80) {
        healthy++;
      } else if (score >= 60) {
        watchlist++;
      } else {
        atRisk++;
      }
    });

    return { healthy, watchlist, atRisk };
  }, [healthSummaryGroups]);

  const HIDE_HEALTH_SUMMARY_CARDS = true;

  useEffect(() => {
    let cancelled = false;

    dispatch(startGetOwners()).then((ownersData) => {
      if (!cancelled) {
        setOwnersByID(ownersData ?? {});
      }
    });

    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  useEffect(() => {
    if (!currentOwnerUid) {
      setAnnotationReadStateByScope({});
      return undefined;
    }

    let cancelled = false;

    dispatch(
      startListenerAnnotationReadState({
        ownerUID: currentOwnerUid,
        onUpdatedDataCallback: (data) => {
          if (!cancelled) {
            setAnnotationReadStateByScope(data ?? {});
          }
        },
        onReferenceAvailable: (ref) => {
          annotationReadStateListenerRef.current = ref;
        },
      })
    );

    return () => {
      cancelled = true;
      dispatch(
        stopListenerAnnotationReadState({
          listenerRef: annotationReadStateListenerRef.current,
        })
      );
      annotationReadStateListenerRef.current = null;
    };
  }, [currentOwnerUid, dispatch]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "flex-start",
        }}
      >
        <ContainerPage
          hasMarginBottom={false}
          sx={{
            flex: 1,
            minWidth: 0,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            overflowX: { xs: "auto", md: "hidden" },
          }}
        >
          {/* Health Summary Cards */}
          {!HIDE_HEALTH_SUMMARY_CARDS && (
            <Box
              sx={{
                width: { xs: "100%" },
                flexShrink: 0,
              }}
            >
              <HealthSummaryCards
                healthSummary={healthSummary}
                isLoading={false}
              />
            </Box>
          )}

          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              mt: HIDE_HEALTH_SUMMARY_CARDS ? 3 : 1,
              mb: 1.5,
              flexWrap: "wrap",
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <TextField
                control={control}
                name="searchTerm"
                placeholder={t(ADMIN_DASHBOARD_SEARCH_PLACEHOLDER)}
                startIcon={
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ color: "#999999 !important", fontSize: 20 }}
                    />
                  </InputAdornment>
                }
                actionsChange={onSearchChange}
                sx={{
                  minWidth: 250,
                  "& .MuiInputBase-input": {
                    padding: "9px 12px",
                  },
                }}
                variant="filled"
                rules={{}}
                errors={{}}
              />
              <Select
                control={control}
                name="filterStatus"
                options={OWNER_DASHBOARD_FILTER_STATUS_OPTIONS}
                doTranslate={true}
                placeholder={t(ADMIN_DASHBOARD_FILTER_STATUS_PLACEHOLDER)}
                size="small"
                actionsChange={onFilterStatusChange}
                sx={{
                  minWidth: 180,
                  mb: 0.2,
                  "& .MuiInputBase-input": {
                    padding: "9px 12px",
                  },
                }}
                variant="filled"
                displayEmpty={true}
                showEmptyOption={false}
                rules={{}}
                errors={{}}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
                width: { xs: "100%", md: "auto" },
                justifyContent: { xs: "flex-start", md: "flex-end" },
              }}
            >
              <Button
                color="secondary"
                variant="contained"
                onClick={() => setIsCreateBrandModalOpen(true)}
              >
                {t(ADMIN_DASHBOARD_CREATE_BRAND_BUTTON)}
              </Button>
            </Box>
          </Box>

          <ClientsTable
            isLoading={isLoading}
            displayedClients={displayedClients}
            groupedClients={groupedClients}
            hasMoreItems={hasMoreItems}
            isLoadingMore={isLoadingMore}
            lastItemRef={lastItemRef}
            totalClients={totalClients}
            onSelectAnnotationTarget={onSelectAnnotationTarget}
            annotationReadStateByScope={annotationReadStateByScope}
          />
        </ContainerPage>

        <AnnotationsSidebar
          key={
            selectedAnnotationTarget
              ? `${selectedAnnotationTarget.advertiserID}-${selectedAnnotationTarget.isAgency}`
              : "annotations-sidebar"
          }
          target={selectedAnnotationTarget}
          isVisible={Boolean(selectedAnnotationTarget)}
          onClose={() => setSelectedAnnotationTarget(null)}
          ownersByID={ownersByID}
        />
      </Box>

      <ModalCreateBrand
        modalOpen={isCreateBrandModalOpen}
        onCloseModal={() => setIsCreateBrandModalOpen(false)}
      />
    </>
  );
};

export default AdminDashboard;
